import AdUtils from '../../utils/ads';
import AnalyticsInstance from '@shared/utils/analytics';
import Category from '@shared/classes/category';
import Channel from '../../classes/channel';
import DeviceConfig from '@devices/config';
import Rail from '../../classes/rail';
import Sections from '../../types/enums/sections';
import { OmnitrackPlaybackInteraction } from '@tvup/omnitrack-js';
import { RailType } from '../../types/enums/rail';
import { clearError, reset } from '../slices/media.slice';
import { clearModal } from '../slices/modal.slice';
import { clearPauseAd, clearPlayAd, setVideoAd } from '../slices/ads.slice';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { setParams, updateStack } from '../slices/router.slice';
import { updateCurrentEvent } from './events.thunk';
import {
  channelDown as channelDownAction,
  channelUp as channelUpAction,
  loadChannels as loadChannelsAction,
  setChannel as setChannelAction,
} from '../slices/channels.slice';
import {
  IChannelsState,
  IPlanState,
  IRouteState,
  IUserState,
} from '../../types/interfaces/states';

const channelDown = createAsyncThunk(
  'channel/down',
  (data: undefined, thunk) => {
    try {
      const { router } = thunk.getState() as {
        router: IRouteState;
        user: IUserState;
      };
      AnalyticsInstance.setPlaybackInteraction(
        OmnitrackPlaybackInteraction.REMOTE
      );
      thunk.dispatch(
        setParams({
          ...router.params.current,
          audio: undefined,
          restart: undefined,
          subtitles: undefined,
        })
      );
      thunk.dispatch(reset());
      thunk.dispatch(clearError());
      thunk.dispatch(clearModal());
      thunk.dispatch(clearPauseAd());
      thunk.dispatch(clearPlayAd());
      thunk.dispatch(channelDownAction());
      thunk.dispatch(updateCurrentEvent());
    } catch (error) {
      thunk.rejectWithValue(error);
    }
  }
);

const channelUp = createAsyncThunk('channel/up', (data: undefined, thunk) => {
  try {
    const { router } = thunk.getState() as {
      router: IRouteState;
      user: IUserState;
    };
    AnalyticsInstance.setPlaybackInteraction(
      OmnitrackPlaybackInteraction.REMOTE
    );
    thunk.dispatch(
      setParams({
        ...router.params.current,
        audio: undefined,
        restart: undefined,
        subtitles: undefined,
      })
    );
    thunk.dispatch(reset());
    thunk.dispatch(clearError());
    thunk.dispatch(clearModal());
    thunk.dispatch(clearPauseAd());
    thunk.dispatch(clearPlayAd());
    thunk.dispatch(channelUpAction());
    thunk.dispatch(updateCurrentEvent());
  } catch (error) {
    thunk.rejectWithValue(error);
  }
});

const loadChannels = createAsyncThunk(
  'load/channel',
  (data: Channel[] | undefined, thunk) => {
    try {
      const {
        channels: { channelsData },
        plans: { anonymous },
        user,
      } = thunk.getState() as {
        channels: IChannelsState;
        user: IUserState;
        plans: IPlanState;
      };
      const anonymousId = anonymous?.id || '';
      const channels = data || channelsData || [];
      thunk.dispatch(
        loadChannelsAction({
          anonymousId,
          channels,
          user,
        })
      );
      thunk.dispatch(updateCurrentEvent());
    } catch (error) {
      thunk.rejectWithValue(error);
    }
  }
);

const setChannel = createAsyncThunk(
  'set/channel',
  (data: number | Channel, thunk) => {
    try {
      const {
        channels: { currentChannel },
      } = thunk.getState() as {
        channels: IChannelsState;
      };
      if (
        (data instanceof Channel && data?.id !== currentChannel?.id) ||
        (typeof data === 'number' && data !== currentChannel?.dial)
      ) {
        thunk.dispatch(clearPauseAd());
        thunk.dispatch(clearPlayAd());
      }
      thunk.dispatch(setChannelAction(data));
      if (!DeviceConfig.capabilities.disableAds.prerolls)
        thunk.dispatch(setVideoAd(AdUtils.getSource(Sections.LIVE)));
      thunk.dispatch(updateCurrentEvent());
    } catch (error) {
      thunk.rejectWithValue(error);
    }
  }
);

const updateChannelRail = createAsyncThunk(
  'update/channelRailParams',
  (data: undefined, thunk) => {
    try {
      const {
        channels,
        router: {
          params: { stack },
        },
      } = thunk.getState() as {
        channels: IChannelsState;
        router: IRouteState;
      };

      // Check if rail is channels rail
      const index = stack?.length ? stack.length - 1 : null;
      const currentRail: Rail | null =
        index !== null
          ? ((stack[index]?.currentRail || null) as Rail | null)
          : null;
      if (index === null || currentRail?.type !== RailType.CHANNEL) {
        return;
      }

      // Update horizontal index before returning to main
      const filter = stack.find((item) => item?.filter)?.filter as
        | Category
        | undefined;
      const horizontalIndex = channels.currentChannel
        ? channels.favouriteChannels
            ?.filter((channel) => {
              if (!filter) return true;
              return (
                channel?.category === filter?.id ||
                channel?.category === filter?.name
              );
            })
            .indexOf(channels.currentChannel)
        : null;

      if (
        horizontalIndex !== null &&
        horizontalIndex !== undefined &&
        horizontalIndex > -1
      ) {
        thunk.dispatch(
          updateStack({
            index,
            params: {
              ...stack[index],
              horizontalIndex,
            },
          })
        );
      }
    } catch (error) {
      thunk.rejectWithValue(error);
    }
  }
);

export { channelDown, channelUp, loadChannels, setChannel, updateChannelRail };
